import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import PhoneTextField from "../../../../components/PhoneTextField";
import RegisterEmailTextField from "../../../../components/RegisterEmailTextField";
import useFormValidity from "../stepUtils";

export interface ContactStepProps {
  handleNext: () => void;
  handleBack: () => void;
  handleCancel: () => void;
}

function ContactStep({
  handleNext,
  handleBack,
  handleCancel,
}: ContactStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const [isFocusEmail, setIsFocusEmail] = useState(false);
  const [isFocusConfirmEmail, setIsFocusConfirmEmail] = useState(false);
  const [isFocusPhone, setIsFocusPhone] = useState(false);
  const [isFocusCellPhone, setIsFocusCellPhone] = useState(false);
  const { control } = useFormContext();
  const isFormsValid = useFormValidity(["email", "confirm_email", "cellphone"]);

  return (
    <Stack alignItems="center" spacing={1}>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => {
          const matches = (() => {
            const result =
              error && error.types ? (error.types.matches as string[]) : [];
            if (!Array.isArray(result)) return [result];
            return result;
          })();
          return (
            <RegisterEmailTextField
              {...field}
              error={Boolean(error)}
              fullWidth
              helperText={
                isFocusEmail && !field.value ? (
                  <Typography
                    color="common.black"
                    component="div"
                    variant="caption"
                  >
                    {t("register.emailHelperText")}
                  </Typography>
                ) : (
                  error?.message || " "
                )
              }
              isDomainError={matches.some((item) => item === "isDomainError")}
              isDotComError={matches.some((item) => item === "isDotComError")}
              isEmail={error?.type === "email"}
              isExistEmail={error?.type === "existingEmail"}
              label={t("register.emailLabel")}
              onBlur={() => {
                setIsFocusEmail(false);
                field.onBlur();
              }}
              onFocus={() => {
                setIsFocusEmail(true);
              }}
              sx={{
                "& .MuiFormLabel-root": {
                  color: "primary.main",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              variant="outlined"
            />
          );
        }}
      />
      <Controller
        control={control}
        name="confirm_email"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            color="primary"
            error={Boolean(error)}
            fullWidth
            helperText={
              isFocusConfirmEmail && !field.value ? (
                <Typography
                  color="common.black"
                  component="div"
                  variant="caption"
                >
                  {t("register.confirmEmailHelperText")}
                </Typography>
              ) : (
                error?.message || " "
              )
            }
            label={t("register.confirmEmailLabel")}
            onBlur={() => {
              setIsFocusConfirmEmail(false);
              field.onBlur();
            }}
            onFocus={() => {
              setIsFocusConfirmEmail(true);
            }}
            sx={{
              "& .MuiFormLabel-root": {
                color: "primary.main",
              },
              "& .MuiInputBase-input": {
                color: "primary.main",
              },
            }}
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name="cellphone"
        render={({ field, fieldState: { error } }) => (
          <PhoneTextField
            {...field}
            error={Boolean(error)}
            fullWidth
            helperText={
              isFocusCellPhone && !field.value ? (
                <Typography
                  color="common.black"
                  component="div"
                  variant="caption"
                >
                  {t("common.numbersOnly")}
                </Typography>
              ) : (
                error?.message || " "
              )
            }
            onBlur={() => {
              setIsFocusCellPhone(false);
              field.onBlur();
            }}
            onFocus={() => {
              setIsFocusCellPhone(true);
            }}
            phoneType="mobile"
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name="phone"
        render={({ field, fieldState: { error } }) => (
          <PhoneTextField
            {...field}
            error={Boolean(error)}
            fullWidth
            helperText={
              isFocusPhone && !field.value ? (
                <Typography
                  color="common.black"
                  component="div"
                  variant="caption"
                >
                  {t("common.numbersOnly")}
                </Typography>
              ) : (
                error?.message || " "
              )
            }
            onBlur={() => {
              setIsFocusPhone(false);
              field.onBlur();
            }}
            onFocus={() => {
              setIsFocusPhone(true);
            }}
            phoneType="telephone"
            variant="outlined"
          />
        )}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        marginTop="5%"
        width="100%"
      >
        <Button
          color="secondary"
          onClick={handleBack}
          size="large"
          tabIndex={-1}
          type="button"
          variant="outlined"
        >
          {t("common.previous")}
        </Button>
        <Button
          color="primary"
          disabled={!isFormsValid}
          onClick={handleNext}
          size="large"
          tabIndex={0}
          type="button"
          variant="contained"
        >
          {t("common.next")}
        </Button>
      </Box>
      <Box textAlign="end" width="100%">
        <Button
          color="success"
          component={Link}
          onClick={handleCancel}
          size="medium"
          sx={{ marginTop: "24px" }}
          to=""
          variant="text"
        >
          {t("common.cancelRegistration")}
        </Button>
      </Box>
    </Stack>
  );
}

export default ContactStep;
